/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import React, { useEffect, useMemo, useState } from 'react';

import { Box, styled, TextField as MuiTextField, TextFieldProps as MuiTextFieldProps, lighten, darken, alpha, Tooltip, TextField, Alert, AlertProps, inputBaseClasses, formLabelClasses, outlinedInputClasses, Typography, BoxProps } from '@mui/material';
import { IFormState, IFieldInputProps, useFormErrorBoundary } from '@ngt/forms-core';
import { IAdditionalFormInputProps } from './Input';
import useErrorStyling, { ErrorClassMapping, ErrorStylingType } from '../../../hooks/form/useErrorStyling';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPencil } from '@fortawesome/pro-solid-svg-icons';
import MuiMarkdown, { Overrides } from 'mui-markdown';
import { FormError } from '../../../errors';
import FormErrorType from '@/types/form/FormErrorType';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

type MarkdownProps = Partial<Omit<MuiTextFieldProps, 'type' | 'multiline'>> & IFieldInputProps<string, any, FormError, string> & IAdditionalFormInputProps

export interface IAlertMarkdownProps extends MarkdownProps {
    severity: AlertProps['severity'],
    FieldContainerProps: BoxProps,
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */
const FieldContainer = styled(Box)(({theme}) => ({
    position: 'relative'
}));

const IconContainer = styled(Box, { shouldForwardProp: propName => propName !== 'fieldMargin' })(({fieldMargin, theme}: any) => ({
    position: 'absolute',
    display: 'flex',
    right: theme.spacing(1),
    top: fieldMargin === 'normal' ? 
        theme.spacing(1):
        fieldMargin === 'dense' ?
            theme.spacing(0):
            theme.spacing(-1),
    zIndex: 20,
    lineHeight: '1',
    fontSize: '0.85rem',
    background: theme.palette.mode === 'dark' ? 
        lighten(theme.palette.common.black, 0.15) :
        darken(theme.palette.common.white, 0.01),
    border: theme.palette.mode === 'dark' ? 
        `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
        `solid 1px ${alpha(theme.palette.common.black, 0.12)}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.25, 0.5, 0.25, 0.5),

    '& > div': {
        margin: theme.spacing(0, 0.5),
    }
}));

const Icon = styled(Box)(({active, theme}: any) => ({
    cursor: 'pointer',
    opacity: active === '1' ? 1 : 0.25
}));

const FormControl = styled(Box)(({active, theme}: any) => ({
    cursor: 'pointer',
    opacity: active === '1' ? 1 : 0.25
}));


const overrides: Overrides = {
    p: {
        component: Typography,
        props: {
            variant: 'body1',
            gutterBottom: true
        }
    },
    li: {
        component: ({children, ...props}) => {
            return (
                <li {...props}>
                    <Typography variant="body1">
                        {children}
                    </Typography>
                </li>
            )
        }
    }
}

const InternalMarkdown = React.forwardRef<HTMLInputElement>(({
    ...rest
}: any, ref) => {

    return (
        <Box {...rest}>
            <input type="hidden" value={rest.value} ref={ref} />
            {!rest.value && (<Box sx={{visibility: 'hidden'}}>hidden</Box>)}
            {!!rest.value && (
                <Alert severity={rest.severity} sx={{ margin: -2}}>
                    <MuiMarkdown overrides={overrides}>{rest.value ?? ''}</MuiMarkdown>
                </Alert>
            )}
        </Box>
    );
});



const StyledMuiTextField = styled(MuiTextField)(({theme, margin}) => ({
    marginTop: margin === 'normal' ?
        theme.spacing(2) :
        margin === 'dense' ?
            theme.spacing(1) :
            theme.spacing(0),
    marginBottom: margin === 'normal' ?
        theme.spacing(1) :
        margin === 'dense' ?
            theme.spacing(0.5) :
            theme.spacing(0),
    [`& .${inputBaseClasses.root}`]: {
        padding: theme.spacing(0)
    },
    [`& .${inputBaseClasses.input}`]: {
        padding: theme.spacing(2, 2)
    },
    [`& .${formLabelClasses.root}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(2)}) scale(1)`
    },
    [`& label.${formLabelClasses.focused}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(-1.25)}) scale(1)`
    },
    [`& label.${formLabelClasses.filled}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(-1.25)}) scale(1)`
    },
    [`& fieldset.${outlinedInputClasses.notchedOutline}`]: {
        padding: theme.spacing(0, 1),

        '& legend': {
            fontSize: '1em',
            '& span': {
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1)
            }
        }
    }
}))
/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const AlertMarkdown: React.FunctionComponent<IAlertMarkdownProps> = ({
    FieldInputProps: { state: { value }, actions: {  update, blur, focus, touch } },
    FieldContainerProps,
    fullWidth,
    onChange,
    sx,
    inputProps: { onFocus: inputPropsFocus, onBlur: inputPropsBlur, ...inputProps } = {},
    hasErrors,
    errors,
    errorType,
    ...rest
}) => {
    const [preview, setPreview] = useState(rest.disabled);

    useEffect(() => {
            setPreview(rest.disabled);
    }, [rest.disabled, setPreview])
    
    const [localValue, setLocalValue] = useState(() => value ?? '');

    useEffect(() => {
        setLocalValue(value ?? '');
    }, [value, setLocalValue])

    const onChangeCombined = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => { 
        setLocalValue(event.target.value);

        if (onChange) {
            onChange(event);
        }

        update(event.target.value && event.target.value !== '' ? event.target.value : undefined as any, true, true, true);
    }, [onChange, update]);

    const onFocusCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsFocus) {
            inputPropsFocus(event);
        }

        focus();
    }, [inputPropsFocus, focus]);

    const onBlurCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsBlur) {
            inputPropsBlur(event);
        }

        blur();
        touch();
    }, [inputPropsBlur, blur, touch]);

    const errorStyling = useErrorStyling(errorType, ErrorStylingType.Input);

    return (
        <FieldContainer
            {...FieldContainerProps}
        >
            <IconContainer
                // @ts-ignore
                fieldMargin={rest.margin}
            >
                <Tooltip title="Edit" placement='top'>
                    <Icon 
                        // @ts-ignore
                        active={preview ? undefined : '1'}
                        onClick={() => setPreview(false)}
                    >
                        <FontAwesomeIcon icon={faPencil} size="sm" />
                    </Icon>
                </Tooltip>
                <Box sx={{opacity: 0.25}}>
                    |
                </Box>
                <Tooltip title="Preview" placement='top'>
                    <Icon 
                        // @ts-ignore
                        active={preview ? '1' : undefined}
                        onClick={() => setPreview(true)}
                    >
                        <FontAwesomeIcon icon={faEye} size="sm" />
                    </Icon>
                </Tooltip>
            </IconContainer>
            {
                !preview && (
                    <StyledMuiTextField
                        type="text"
                        {...rest as any}
                        fullWidth={fullWidth === undefined ? true : fullWidth}
                        inputProps={{
                            ...inputProps,
                            onBlur: onBlurCombined,
                            onFocus: onFocusCombined
                        }}
                        value={localValue ?? ''}
                        onChange={onChangeCombined}
                        multiline
                        sx={{...sx, ...errorStyling}}
                        error={hasErrors}
                        helperText={
                            <>
                                {rest.helperText && (<span className={ErrorClassMapping[FormErrorType.HelperText]}>{rest.helperText}</span>)}
                                {errors && errors.length > 0 && errors?.map((error, i) => (<span className={ErrorClassMapping[error.type!]} key={i}>{error.message}</span>))}
                            </>
                        }
                    />
                ) 
            }
            {
                preview && (
                    
                    <StyledMuiTextField
                        type="text"
                        {...rest as any}
                        fullWidth={fullWidth === undefined ? true : fullWidth}
                        inputProps={{
                            ...inputProps,
                            onBlur: onBlurCombined,
                            onFocus: onFocusCombined,
                            severity: rest.severity
                        }}
                        InputProps={{
                            ...rest.InputProps,
                            inputComponent: InternalMarkdown
                        }}
                        value={localValue ?? ''}
                        onChange={onChangeCombined}
                        multiline
                        sx={{...sx, ...errorStyling}}
                        error={hasErrors}
                        helperText={
                            <>
                                {rest.helperText && (<span className={ErrorClassMapping[FormErrorType.HelperText]}>{rest.helperText}</span>)} 
                                {errors && errors.length > 0 && errors?.map((error, i) => (<span className={ErrorClassMapping[error.type!]} key={i}>{error.message}</span>))}
                            </>
                        }
                    >
                    </StyledMuiTextField>
                )
            }
        </FieldContainer>
    );
}

const Preview: React.FunctionComponent<{ children?: string }> = ({ children }) => {
    return (
        <MuiMarkdown overrides={overrides}>{children ?? ''}</MuiMarkdown>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default Object.assign(AlertMarkdown, { Preview: Preview });
